import React, { useEffect, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  getDocTypes, getNextDocNumber, generateDocument,
  createDocument, updateDocument, getDocument, exportFile,
  getBrand, logoUrl,
} from "@/lib/api";
import DocumentPreview from "@/components/DocumentPreview";
import FlowchartEditor from "@/components/FlowchartEditor";
import LibraryPanel from "@/components/LibraryPanel";
import BrandSettings from "@/components/BrandSettings";
import InviteManager from "@/components/InviteManager";
import ModeNav from "@/components/ModeNav";
import BrandMark from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { authLogout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Save, FileDown, GitBranch, PenLine, Library, FileText, Loader2, Settings, UserPlus, LogOut,
} from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

const Generator = () => {
  const { user, isAdmin, logout } = useAuth();
  const [docTypes, setDocTypes] = useState([]);
  const [docType, setDocType] = useState("SOP");
  const [title, setTitle] = useState("");
  const [rawText, setRawText] = useState("");
  const [metadata, setMetadata] = useState({ document_number: "", version: "1.0", date: todayISO() });
  const [sections, setSections] = useState([]);
  const [flowchart, setFlowchart] = useState([]);
  const [currentDocId, setCurrentDocId] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportPdf, setExportPdf] = useState(true);
  const [exportDocx, setExportDocx] = useState(false);
  const [tab, setTab] = useState("compose");
  const [libRefresh, setLibRefresh] = useState(0);

  const [brand, setBrand] = useState({ accent_color: "#002FA7", footer_text: "Confidential Internal Document", has_logo: false });
  const [brandOpen, setBrandOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [logoVersion, setLogoVersion] = useState(Date.now());

  const handleLogout = async () => {
    try { await authLogout(); } catch (e) {}
    logout();
  };

  const accent = brand.accent_color || "#002FA7";
  const docTypeLabel = docTypes.find((d) => d.key === docType)?.label || docType;

  const loadBrand = useCallback(async () => {
    try {
      const b = await getBrand();
      setBrand(b);
      setLogoVersion(Date.now());
    } catch (e) {}
  }, []);

  const refreshDocNumber = useCallback(async (dt) => {
    try {
      const { document_number } = await getNextDocNumber(dt);
      setMetadata((m) => ({ ...m, document_number }));
    } catch (e) {}
  }, []);

  useEffect(() => {
    getDocTypes().then(setDocTypes);
    refreshDocNumber("SOP");
    loadBrand();
  }, [refreshDocNumber, loadBrand]);

  const onDocTypeChange = (v) => {
    setDocType(v);
    if (!currentDocId) refreshDocNumber(v);
  };

  const handleGenerate = async () => {
    if (!rawText.trim()) {
      toast.error("Please paste some rough notes first.");
      return;
    }
    setGenerating(true);
    const t = toast.loading("Claude is structuring your document…");
    try {
      const res = await generateDocument({ doc_type: docType, raw_text: rawText, title: title || null });
      setTitle(res.title);
      setSections(res.sections);
      setFlowchart(res.flowchart);
      toast.success("Document generated", { id: t });
      setTab("sections");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed", { id: t });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (sections.length === 0) {
      toast.error("Generate or add content before saving.");
      return;
    }
    setSaving(true);
    try {
      const payload = { title, doc_type: docType, raw_text: rawText, metadata, sections, flowchart };
      let doc;
      if (currentDocId) {
        doc = await updateDocument(currentDocId, payload);
        toast.success(`Saved as version ${doc.current_version}`);
      } else {
        doc = await createDocument(payload);
        setCurrentDocId(doc.id);
        toast.success("Document saved to library");
      }
      setLibRefresh((n) => n + 1);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (sections.length === 0) {
      toast.error("Nothing to export yet.");
      return;
    }
    if (!exportPdf && !exportDocx) {
      toast.error("Select at least one format (PDF or Word).");
      return;
    }
    setExporting(true);
    try {
      let flowImg = null;
      const el = document.getElementById("flowchart-capture");
      if (el && flowchart.length > 0) {
        try {
          flowImg = await toPng(el, { backgroundColor: "#ffffff", pixelRatio: 2 });
        } catch (e) {}
      }
      const payload = { title, doc_type: docType, metadata, sections, flowchart_image: flowImg };
      if (exportPdf) await exportFile("pdf", payload);
      if (exportDocx) await exportFile("docx", payload);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const loadDoc = async (d) => {
    const full = await getDocument(d.id);
    setCurrentDocId(full.id);
    setDocType(full.doc_type);
    setTitle(full.title);
    setRawText(full.raw_text || "");
    setMetadata(full.metadata);
    setSections(full.sections);
    setFlowchart(full.flowchart || []);
    setTab("compose");
    toast.success(`Loaded "${full.title}"`);
  };

  const newDoc = () => {
    setCurrentDocId(null);
    setTitle("");
    setRawText("");
    setSections([]);
    setFlowchart([]);
    setMetadata({ document_number: "", version: "1.0", date: todayISO() });
    refreshDocNumber(docType);
    setTab("compose");
  };

  return (
    <div className="h-screen w-full flex overflow-hidden bg-slate-100 text-slate-900">
      {/* Sidebar */}
      <aside className="w-full md:w-[420px] lg:w-[460px] h-full flex flex-col border-r border-slate-200 bg-white z-10 shrink-0">
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandMark height={24} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 border-l border-slate-200 pl-2.5 leading-tight">Document<br />Generator</div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <>
                <Button variant="ghost" size="icon" onClick={() => setInviteOpen(true)} data-testid="invite-btn" className="h-8 w-8" title="Invite Team">
                  <UserPlus size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { loadBrand(); setBrandOpen(true); }} data-testid="brand-settings-btn" className="h-8 w-8" title="Brand Settings">
                  <Settings size={16} />
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={newDoc} data-testid="new-doc-btn" className="text-xs">
              New
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <ModeNav accent={accent} />
          <TabsList className="mx-6 mt-3 grid grid-cols-4 rounded-md bg-slate-100">
            <TabsTrigger value="compose" data-testid="tab-compose" className="text-xs gap-1.5"><PenLine size={13} /> Compose</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections" className="text-xs gap-1.5"><FileText size={13} /> Sections</TabsTrigger>
            <TabsTrigger value="flowchart" data-testid="tab-flowchart" className="text-xs gap-1.5"><GitBranch size={13} /> Flow</TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-library" className="text-xs gap-1.5"><Library size={13} /> Library</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <TabsContent value="compose" className="mt-0 space-y-4">
              <div>
                <Label className="label-caps">Document Type</Label>
                <Select value={docType} onValueChange={onDocTypeChange}>
                  <SelectTrigger className="mt-1.5" data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {docTypes.map((d) => (
                      <SelectItem key={d.key} value={d.key} data-testid={`doc-type-${d.key}`}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="label-caps">Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Auto-generated if left blank"
                  className="mt-1.5"
                  data-testid="title-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="label-caps">Doc No.</Label>
                  <Input
                    value={metadata.document_number}
                    onChange={(e) => setMetadata({ ...metadata, document_number: e.target.value })}
                    className="mt-1.5 text-xs"
                    data-testid="doc-number-input"
                  />
                </div>
                <div>
                  <Label className="label-caps">Version</Label>
                  <Input
                    value={metadata.version}
                    onChange={(e) => setMetadata({ ...metadata, version: e.target.value })}
                    className="mt-1.5 text-xs"
                    data-testid="version-input"
                  />
                </div>
                <div>
                  <Label className="label-caps">Date</Label>
                  <Input
                    type="date"
                    value={metadata.date}
                    onChange={(e) => setMetadata({ ...metadata, date: e.target.value })}
                    className="mt-1.5 text-xs"
                    data-testid="date-input"
                  />
                </div>
              </div>

              <div>
                <Label className="label-caps">Rough Notes</Label>
                <Textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste your rough plain-text notes here. Describe the process, steps, rules, or points — Claude will structure them into a professional document."
                  className="mt-1.5 min-h-[240px] text-sm leading-relaxed resize-none"
                  data-testid="raw-text-input"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full gap-2 text-white transition-all hover:-translate-y-[1px] hover:brightness-110"
                style={{ background: accent }}
                data-testid="generate-doc-btn"
              >
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {generating ? "Structuring…" : "Generate Document"}
              </Button>
            </TabsContent>

            <TabsContent value="sections" className="mt-0 space-y-4">
              {sections.length === 0 ? (
                <div className="text-sm text-slate-400 py-12 text-center border border-dashed border-slate-200 rounded-md">
                  No sections yet. Generate a document first.
                </div>
              ) : (
                sections.map((s, i) => (
                  <div key={i} className="space-y-1.5" data-testid={`section-edit-${i}`}>
                    <Input
                      value={s.heading}
                      onChange={(e) => {
                        const n = [...sections];
                        n[i] = { ...n[i], heading: e.target.value };
                        setSections(n);
                      }}
                      className="font-semibold text-sm"
                      data-testid={`section-heading-${i}`}
                    />
                    <Textarea
                      value={s.content}
                      onChange={(e) => {
                        const n = [...sections];
                        n[i] = { ...n[i], content: e.target.value };
                        setSections(n);
                      }}
                      className="text-sm min-h-[120px] resize-none"
                      data-testid={`section-content-${i}`}
                    />
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="flowchart" className="mt-0">
              <FlowchartEditor flowchart={flowchart} setFlowchart={setFlowchart} />
            </TabsContent>

            <TabsContent value="library" className="mt-0">
              <LibraryPanel refreshKey={libRefresh} onLoad={loadDoc} />
            </TabsContent>
          </div>
        </Tabs>

        {/* User bar */}
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between" data-testid="user-bar">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: accent }}>
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate leading-tight" data-testid="user-name">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">{isAdmin ? "Administrator" : "Member"}</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid="logout-btn" title="Sign out">
            <LogOut size={16} />
          </Button>
        </div>
      </aside>

      {/* Preview */}
      <main className="flex-1 h-full flex flex-col overflow-hidden">
        {/* Export bar */}
        <div className="h-16 border-b border-slate-200 bg-white px-8 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <span className="label-caps">Export</span>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={exportPdf} onCheckedChange={setExportPdf} data-testid="export-pdf-checkbox" />
              PDF
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={exportDocx} onCheckedChange={setExportDocx} data-testid="export-docx-checkbox" />
              Word (.docx)
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-doc-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {currentDocId ? "Save Version" : "Save"}
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="gap-2 text-white transition-all hover:-translate-y-[1px] hover:brightness-110"
              style={{ background: accent }}
              data-testid="export-btn"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
              Export
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-100 p-8 lg:p-12">
          <DocumentPreview
            title={title}
            docTypeLabel={docTypeLabel}
            metadata={metadata}
            sections={sections}
            flowchart={flowchart}
            accentColor={accent}
            footerText={brand.footer_text}
            logoUrl={brand.has_logo ? `${logoUrl()}?v=${logoVersion}` : null}
          />
        </div>
      </main>

      <BrandSettings open={brandOpen} onOpenChange={setBrandOpen} brand={brand} onSaved={loadBrand} />
      <InviteManager open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
};

export default Generator;
